import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Spinner, Alert } from 'react-bootstrap';
import { QRCodeSVG } from 'qrcode.react';
import { mfaSetup, mfaVerifySetup } from '../../../Controllers/AuthController';

function MfaSetup({
  username,
  session,
  setError,
  setIsAuthed,
  onLoginSuccess,
}) {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [setupLoading, setSetupLoading] = useState(true);
  const [secretCode, setSecretCode] = useState('');
  const [currentSession, setCurrentSession] = useState(session);
  const [setupError, setSetupError] = useState('');

  // Generate QR code URL for authenticator apps
  const qrCodeUrl = secretCode
    ? `otpauth://totp/PickupHungary:${username}?secret=${secretCode}&issuer=PickupHungary`
    : '';

  useEffect(() => {
    const initMfaSetup = async () => {
      try {
        setSetupLoading(true);
        const result = await mfaSetup(session, username);
        if (result && result.secretCode) {
          setSecretCode(result.secretCode);
          setCurrentSession(result.session);
        } else {
          setSetupError('Failed to initialize MFA setup. Please try logging in again.');
        }
      } catch (err) {
        setSetupError(err.message || 'Failed to initialize MFA setup.');
      } finally {
        setSetupLoading(false);
      }
    };

    initMfaSetup();
  }, [session, username]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError('');

    try {
      const result = await mfaVerifySetup(currentSession, code, username);

      if (result === true) {
        // Call authenticate to fetch session data before setting isAuthed
        if (onLoginSuccess) {
          await onLoginSuccess();
        }
        setIsAuthed(true);
        return;
      }

      setError('Failed to verify code. Please try again.');
    } catch (err) {
      setError(err.message || 'Verification failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Auto-submit when 6 digits are entered
  const handleCodeChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setCode(value);
  };

  if (setupLoading) {
    return (
      <div className="text-center py-4">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3 text-muted">Setting up two-factor authentication...</p>
      </div>
    );
  }

  if (setupError) {
    return (
      <Alert variant="danger">
        <Alert.Heading>Setup Error</Alert.Heading>
        <p>{setupError}</p>
      </Alert>
    );
  }

  return (
    <>
      <h4 className="card-title text-center mb-3">Set Up Two-Factor Authentication</h4>

      <div className="text-center mb-4">
        <p className="text-muted mb-3">
          Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.)
        </p>

        {qrCodeUrl && (
          <div className="d-inline-block p-3 bg-white rounded border">
            <QRCodeSVG value={qrCodeUrl} size={180} />
          </div>
        )}

        <details className="mt-3">
          <summary className="text-muted" style={{ cursor: 'pointer' }}>
            Can&apos;t scan? Enter code manually
          </summary>
          <div className="mt-2 p-2 bg-light rounded">
            <code className="text-break" style={{ fontSize: '0.85rem' }}>{secretCode}</code>
          </div>
        </details>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="mb-3">
          <label htmlFor="mfaCode" className="form-label">
            Enter the 6-digit code from your authenticator app
          </label>
          <input
            type="text"
            className="form-control form-control-lg text-center"
            id="mfaCode"
            value={code}
            onChange={handleCodeChange}
            placeholder="000000"
            maxLength={6}
            pattern="[0-9]{6}"
            autoComplete="one-time-code"
            inputMode="numeric"
            required
            style={{ letterSpacing: '0.5em', fontFamily: 'monospace' }}
          />
        </div>
        <button
          type="submit"
          disabled={loading || code.length !== 6}
          className="btn btn-primary w-100"
        >
          {loading ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Verifying...
            </>
          ) : (
            'Verify & Complete Setup'
          )}
        </button>
      </form>
    </>
  );
}

MfaSetup.propTypes = {
  username: PropTypes.string.isRequired,
  session: PropTypes.string.isRequired,
  setError: PropTypes.func.isRequired,
  setIsAuthed: PropTypes.func.isRequired,
  onLoginSuccess: PropTypes.func.isRequired,
};

export default MfaSetup;
